import { BrainCircuit, Info, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';

import { Accordion } from './Accordion';
import { Tabs } from './Tabs';
import {
    detectorIds,
    featureNeedsLandmarks,
    getFeatureDisabledReason,
    modelCatalog,
    type AnalysisConfig,
    type DetectorId,
    type FeatureId,
    type ModelId,
    type OutputSettings,
} from '../domain/analysis';
import { useTranslate } from '../i18n/useI18n';

import type { AccordionSection } from './Accordion';
import type { MediaRole } from '../domain/media';
import type { MessageKey } from '../i18n/messages';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';

type SettingsTabId = 'analysis' | 'detector' | 'drawing' | 'models';
type SettingsSectionId =
    | 'detection'
    | 'features'
    | 'matching'
    | 'ssd'
    | 'tiny'
    | 'mtcnn'
    | 'layers'
    | 'colors'
    | 'sizes'
    | 'source'
    | 'catalog';

const modelNameKeys: Readonly<Record<ModelId, MessageKey>> = {
    tinyFaceDetector: 'model.tinyFaceDetector',
    ssdMobilenetv1: 'model.ssdMobilenetv1',
    mtcnn: 'model.mtcnn',
    tinyYolov2: 'model.tinyYolov2',
    faceLandmark68Net: 'model.faceLandmark68Net',
    faceLandmark68TinyNet: 'model.faceLandmark68TinyNet',
    faceRecognitionNet: 'model.faceRecognitionNet',
    faceExpressionNet: 'model.faceExpressionNet',
    ageGenderNet: 'model.ageGenderNet',
};

const colorFields = [
    ['settings.color.box', 'boxColor'],
    ['settings.color.match', 'matchColor'],
    ['settings.color.unknown', 'unknownColor'],
    ['settings.color.landmarkLine', 'landmarkLineColor'],
    ['settings.color.landmarkPoint', 'landmarkPointColor'],
] as const;

const featureIds: readonly FeatureId[] = ['matching', 'landmarks', 'expressions', 'ageAndGender'];

function featureKey(feature: FeatureId, part: 'label' | 'description'): MessageKey {
    return `settings.feature.${feature}.${part}`;
}

function NumberField({
    label,
    value,
    min,
    max,
    step,
    disabled,
    onChange,
}: {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    disabled?: boolean;
    onChange: (value: number) => void;
}): ReactElement {
    return (
        <label className='compact-field'>
            <span>{label}</span>
            <input
                type='number'
                value={value}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                onChange={(event) => {
                    onChange(Number(event.target.value));
                }}
            />
        </label>
    );
}

function Toggle({
    checked,
    disabled,
    label,
    description,
    onChange,
}: {
    checked: boolean;
    disabled?: boolean;
    label: string;
    description?: string;
    onChange: (checked: boolean) => void;
}): ReactElement {
    return (
        <label className={`toggle-row${disabled === true ? ' toggle-row--disabled' : ''}`}>
            <input
                type='checkbox'
                checked={checked}
                disabled={disabled}
                onChange={(event) => {
                    onChange(event.target.checked);
                }}
            />
            <span
                className='switch'
                aria-hidden='true'
            />
            <span className='toggle-copy'>
                <strong>{label}</strong>
                {description !== undefined && description.length > 0 && (
                    <small>{description}</small>
                )}
            </span>
        </label>
    );
}

function SettingsFields({
    disabled,
    children,
}: {
    disabled: boolean;
    children: ReactNode;
}): ReactElement {
    return (
        <fieldset
            className='settings-fields'
            disabled={disabled}
        >
            {children}
        </fieldset>
    );
}

export function SettingsPanel({
    config,
    isRunning,
    canRun,
    runHint,
    onChange,
    onOutputChange,
    onRun,
}: {
    config: AnalysisConfig;
    isRunning: boolean;
    canRun: boolean;
    runHint: string;
    onChange: (config: AnalysisConfig) => void;
    onOutputChange: (output: OutputSettings) => void;
    onRun: () => void;
}): ReactElement {
    const translate = useTranslate();
    const [activeTab, setActiveTab] = useState<SettingsTabId>('analysis');
    const [openSection, setOpenSection] = useState<SettingsSectionId | null>('detection');
    const patch = (next: Partial<AnalysisConfig>): void => {
        onChange({ ...config, ...next });
    };
    const patchSsd = (next: Partial<AnalysisConfig['detectorSettings']['ssd']>): void => {
        patch({
            detectorSettings: {
                ...config.detectorSettings,
                ssd: { ...config.detectorSettings.ssd, ...next },
            },
        });
    };
    const patchTiny = (next: Partial<AnalysisConfig['detectorSettings']['tiny']>): void => {
        patch({
            detectorSettings: {
                ...config.detectorSettings,
                tiny: { ...config.detectorSettings.tiny, ...next },
            },
        });
    };
    const patchMtcnn = (next: Partial<AnalysisConfig['detectorSettings']['mtcnn']>): void => {
        patch({
            detectorSettings: {
                ...config.detectorSettings,
                mtcnn: { ...config.detectorSettings.mtcnn, ...next },
            },
        });
    };
    const patchOutput = (next: Partial<OutputSettings>): void => {
        onOutputChange({ ...config.output, ...next });
    };
    const patchOverlay = (role: MediaRole, drawOverlay: boolean): void => {
        patchOutput({ drawOverlay: { ...config.output.drawOverlay, [role]: drawOverlay } });
    };
    const toggleFeature = (feature: FeatureId, enabled: boolean): void => {
        patch({ features: { ...config.features, [feature]: enabled } });
    };
    const tinyActive = config.detector === 'tinyFaceDetector' || config.detector === 'tinyYolov2';
    const mtcnnActive = config.detector === 'mtcnn';
    const ssdActive = config.detector === 'ssdMobilenetv1';
    const landmarksRequired = featureNeedsLandmarks(config);
    const auxiliaryActive = config.features.expressions || config.features.ageAndGender;
    const overlayHidden =
        !config.output.drawOverlay.reference && !config.output.drawOverlay.haystack;

    const updateThreshold = (index: number, value: number): void => {
        const scoreThresholds = [...config.detectorSettings.mtcnn.scoreThresholds] as [
            number,
            number,
            number,
        ];
        scoreThresholds[index] = value;
        patchMtcnn({ scoreThresholds });
    };

    const section = (
        id: SettingsSectionId,
        title: string,
        content: ReactNode
    ): AccordionSection<SettingsSectionId> => ({
        id,
        title,
        content: <SettingsFields disabled={isRunning}>{content}</SettingsFields>,
    });
    const accordion = (sections: readonly AccordionSection<SettingsSectionId>[]): ReactElement => (
        <Accordion
            sections={sections}
            openSection={openSection}
            onOpenSectionChange={setOpenSection}
        />
    );
    const detectorInactiveNote = (active: boolean): ReactNode =>
        !active && (
            <small className='disabled-reason'>{translate('settings.detectorInactive')}</small>
        );

    const analysisSections = [
        section(
            'detection',
            translate('settings.section.detection'),
            <>
                <label className='field'>
                    <span>{translate('settings.detector')}</span>
                    <select
                        value={config.detector}
                        onChange={(event) => {
                            patch({ detector: event.target.value as DetectorId });
                        }}
                    >
                        {detectorIds.map((id) => {
                            const model = modelCatalog.find((entry) => entry.id === id);
                            const bundled = model?.bundled === true;
                            return (
                                <option
                                    key={id}
                                    value={id}
                                    disabled={!bundled}
                                >
                                    {translate(modelNameKeys[id])}
                                    {!bundled ? ` — ${translate('settings.weightsMissing')}` : ''}
                                </option>
                            );
                        })}
                    </select>
                </label>
                <label className='compact-field'>
                    <span>{translate('settings.maxDimension')}</span>
                    <select
                        value={config.maxInputDimension}
                        onChange={(event) => {
                            patch({ maxInputDimension: Number(event.target.value) });
                        }}
                    >
                        <option value={320}>320 px · {translate('settings.fast')}</option>
                        <option value={480}>480 px</option>
                        <option value={640}>640 px · {translate('settings.recommended')}</option>
                        <option value={960}>960 px</option>
                        <option value={1280}>1280 px</option>
                        <option value={0}>{translate('settings.originalSize')}</option>
                    </select>
                </label>
                <small className='field-help'>{translate('settings.dimensionHelp')}</small>
            </>
        ),
        section(
            'features',
            translate('settings.section.features'),
            <>
                <fieldset className='feature-list'>
                    <legend>{translate('settings.outputs')}</legend>
                    {featureIds.map((feature) => {
                        const reason = getFeatureDisabledReason(feature, config);
                        return (
                            <div key={feature}>
                                <Toggle
                                    checked={config.features[feature]}
                                    disabled={Boolean(reason)}
                                    label={translate(featureKey(feature, 'label'))}
                                    description={translate(featureKey(feature, 'description'))}
                                    onChange={(checked) => {
                                        toggleFeature(feature, checked);
                                    }}
                                />
                                {reason !== undefined && (
                                    <small className='disabled-reason'>{reason}</small>
                                )}
                            </div>
                        );
                    })}
                </fieldset>
                <label className='field'>
                    <span>{translate('settings.landmarkModel')}</span>
                    <select
                        value={config.landmarkModel}
                        disabled={!landmarksRequired}
                        onChange={(event) => {
                            patch({
                                landmarkModel: event.target
                                    .value as AnalysisConfig['landmarkModel'],
                            });
                        }}
                    >
                        <option value='faceLandmark68Net'>
                            {translate('settings.landmarkFull')}
                        </option>
                        <option value='faceLandmark68TinyNet'>
                            {translate('settings.landmarkTiny')}
                        </option>
                    </select>
                    {!landmarksRequired && (
                        <small className='disabled-reason'>
                            {translate('settings.landmarkNotRequired')}
                        </small>
                    )}
                </label>
            </>
        ),
        section(
            'matching',
            translate('settings.section.matching'),
            <>
                <Toggle
                    checked={config.features.matching || config.alignAuxiliaryModels}
                    disabled={config.features.matching || !auxiliaryActive}
                    label={translate('settings.align')}
                    description={translate('settings.alignDescription')}
                    onChange={(checked) => {
                        patch({ alignAuxiliaryModels: checked });
                    }}
                />
                <NumberField
                    label={translate('settings.distanceThreshold')}
                    value={config.distanceThreshold}
                    min={0.1}
                    max={1}
                    step={0.01}
                    disabled={!config.features.matching}
                    onChange={(distanceThreshold) => {
                        patch({ distanceThreshold });
                    }}
                />
            </>
        ),
    ];

    const detectorSections = [
        section(
            'ssd',
            translate('model.ssdMobilenetv1'),
            <>
                {detectorInactiveNote(ssdActive)}
                <NumberField
                    label={translate('settings.minimumConfidence')}
                    value={config.detectorSettings.ssd.minConfidence}
                    min={0.01}
                    max={0.99}
                    step={0.01}
                    disabled={!ssdActive}
                    onChange={(minConfidence) => {
                        patchSsd({ minConfidence });
                    }}
                />
                <NumberField
                    label={translate('settings.maximumResults')}
                    value={config.detectorSettings.ssd.maxResults}
                    min={1}
                    max={1000}
                    step={1}
                    disabled={!ssdActive}
                    onChange={(maxResults) => {
                        patchSsd({ maxResults });
                    }}
                />
            </>
        ),
        section(
            'tiny',
            translate('settings.section.tinyDetectors'),
            <>
                {detectorInactiveNote(tinyActive)}
                <label className='compact-field'>
                    <span>{translate('settings.inputSize')}</span>
                    <select
                        value={config.detectorSettings.tiny.inputSize}
                        disabled={!tinyActive}
                        onChange={(event) => {
                            patchTiny({ inputSize: Number(event.target.value) });
                        }}
                    >
                        {[128, 160, 224, 320, 416, 512, 608].map((size) => (
                            <option
                                key={size}
                                value={size}
                            >
                                {size} px
                            </option>
                        ))}
                    </select>
                </label>
                <NumberField
                    label={translate('settings.scoreThreshold')}
                    value={config.detectorSettings.tiny.scoreThreshold}
                    min={0.01}
                    max={0.99}
                    step={0.01}
                    disabled={!tinyActive}
                    onChange={(scoreThreshold) => {
                        patchTiny({ scoreThreshold });
                    }}
                />
            </>
        ),
        section(
            'mtcnn',
            translate('model.mtcnn'),
            <>
                {detectorInactiveNote(mtcnnActive)}
                <NumberField
                    label={translate('settings.minimumFaceSize')}
                    value={config.detectorSettings.mtcnn.minFaceSize}
                    min={1}
                    max={2048}
                    step={1}
                    disabled={!mtcnnActive}
                    onChange={(minFaceSize) => {
                        patchMtcnn({ minFaceSize });
                    }}
                />
                <NumberField
                    label={translate('settings.scaleFactor')}
                    value={config.detectorSettings.mtcnn.scaleFactor}
                    min={0.01}
                    max={0.99}
                    step={0.001}
                    disabled={!mtcnnActive}
                    onChange={(scaleFactor) => {
                        patchMtcnn({ scaleFactor });
                    }}
                />
                <NumberField
                    label={translate('settings.maximumScales')}
                    value={config.detectorSettings.mtcnn.maxNumScales}
                    min={1}
                    max={100}
                    step={1}
                    disabled={!mtcnnActive}
                    onChange={(maxNumScales) => {
                        patchMtcnn({ maxNumScales });
                    }}
                />
                {[0, 1, 2].map((index) => (
                    <NumberField
                        key={index}
                        label={translate('settings.stageThreshold', { stage: index + 1 })}
                        value={config.detectorSettings.mtcnn.scoreThresholds[index] ?? 0.7}
                        min={0.01}
                        max={0.99}
                        step={0.01}
                        disabled={!mtcnnActive}
                        onChange={(value) => {
                            updateThreshold(index, value);
                        }}
                    />
                ))}
                <Toggle
                    checked={config.detectorSettings.mtcnn.useCustomScaleSteps}
                    disabled={!mtcnnActive}
                    label={translate('settings.customScaleSteps')}
                    onChange={(useCustomScaleSteps) => {
                        patchMtcnn({ useCustomScaleSteps });
                    }}
                />
                <label className='compact-field'>
                    <span>{translate('settings.commaSeparatedSteps')}</span>
                    <input
                        type='text'
                        value={config.detectorSettings.mtcnn.scaleSteps}
                        placeholder='0.9, 0.8, 0.7'
                        disabled={
                            !mtcnnActive || !config.detectorSettings.mtcnn.useCustomScaleSteps
                        }
                        onChange={(event) => {
                            patchMtcnn({ scaleSteps: event.target.value });
                        }}
                    />
                </label>
            </>
        ),
    ];

    const drawingSections = [
        section(
            'layers',
            translate('settings.section.layers'),
            <>
                <Toggle
                    checked={config.output.drawOverlay.reference}
                    label={translate('settings.overlay.reference')}
                    onChange={(drawOverlay) => {
                        patchOverlay('reference', drawOverlay);
                    }}
                />
                <Toggle
                    checked={config.output.drawOverlay.haystack}
                    label={translate('settings.overlay.haystack')}
                    onChange={(drawOverlay) => {
                        patchOverlay('haystack', drawOverlay);
                    }}
                />
                <Toggle
                    checked={config.output.drawUnknownFaces}
                    disabled={!config.output.drawOverlay.haystack || !config.features.matching}
                    label={translate('settings.unknownFaces')}
                    description={translate('settings.unknownFacesDescription')}
                    onChange={(drawUnknownFaces) => {
                        patchOutput({ drawUnknownFaces });
                    }}
                />
                <Toggle
                    checked={config.output.drawBoxes}
                    disabled={overlayHidden}
                    label={translate('settings.boxes')}
                    onChange={(drawBoxes) => {
                        patchOutput({ drawBoxes });
                    }}
                />
                <Toggle
                    checked={config.output.drawScores}
                    disabled={overlayHidden}
                    label={translate('settings.scores')}
                    onChange={(drawScores) => {
                        patchOutput({ drawScores });
                    }}
                />
                <Toggle
                    checked={config.output.drawLabels}
                    disabled={overlayHidden}
                    label={translate('settings.labels')}
                    onChange={(drawLabels) => {
                        patchOutput({ drawLabels });
                    }}
                />
                <Toggle
                    checked={config.output.drawLandmarkLines}
                    disabled={overlayHidden || !config.features.landmarks}
                    label={translate('settings.landmarkLines')}
                    onChange={(drawLandmarkLines) => {
                        patchOutput({ drawLandmarkLines });
                    }}
                />
                <Toggle
                    checked={config.output.drawLandmarkPoints}
                    disabled={overlayHidden || !config.features.landmarks}
                    label={translate('settings.landmarkPoints')}
                    onChange={(drawLandmarkPoints) => {
                        patchOutput({ drawLandmarkPoints });
                    }}
                />
                <small className='field-help'>{translate('settings.drawingHelp')}</small>
            </>
        ),
        section(
            'colors',
            translate('settings.section.colors'),
            <div className='color-grid'>
                {colorFields.map(([label, key]) => (
                    <label
                        className='color-field'
                        key={key}
                    >
                        <span>{translate(label)}</span>
                        <input
                            type='color'
                            value={config.output[key]}
                            onChange={(event) => {
                                patchOutput({ [key]: event.target.value });
                            }}
                        />
                    </label>
                ))}
            </div>
        ),
        section(
            'sizes',
            translate('settings.section.sizes'),
            <>
                <NumberField
                    label={translate('settings.lineWidth')}
                    value={config.output.lineWidth}
                    min={1}
                    max={12}
                    step={1}
                    onChange={(lineWidth) => {
                        patchOutput({ lineWidth });
                    }}
                />
                <NumberField
                    label={translate('settings.pointSize')}
                    value={config.output.pointSize}
                    min={1}
                    max={12}
                    step={1}
                    disabled={!config.features.landmarks}
                    onChange={(pointSize) => {
                        patchOutput({ pointSize });
                    }}
                />
                <NumberField
                    label={translate('settings.fontSize')}
                    value={config.output.fontSize}
                    min={10}
                    max={48}
                    step={1}
                    onChange={(fontSize) => {
                        patchOutput({ fontSize });
                    }}
                />
                <NumberField
                    label={translate('settings.expressionProbability')}
                    value={config.output.expressionMinProbability}
                    min={0.01}
                    max={1}
                    step={0.01}
                    disabled={!config.features.expressions}
                    onChange={(expressionMinProbability) => {
                        patchOutput({ expressionMinProbability });
                    }}
                />
            </>
        ),
    ];

    const modelSections = [
        section(
            'source',
            translate('settings.modelSource'),
            <label className='field'>
                <span>{translate('settings.modelRootUrl')}</span>
                <input
                    type='text'
                    value={config.modelBaseUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        patch({ modelBaseUrl: event.target.value });
                    }}
                />
                <small className='field-help'>
                    <Info aria-hidden='true' /> {translate('settings.modelSourceHelp')}
                </small>
            </label>
        ),
        section(
            'catalog',
            translate('settings.section.catalog'),
            <div className='model-catalog'>
                {modelCatalog.map((model) => (
                    <div
                        key={model.id}
                        className={!model.bundled ? 'is-unavailable' : ''}
                    >
                        <span>{translate(modelNameKeys[model.id])}</span>
                        <small>
                            {translate(
                                model.bundled ? 'settings.modelReady' : 'settings.modelUnavailable'
                            )}
                        </small>
                    </div>
                ))}
            </div>
        ),
    ];

    return (
        <aside className='control-panel'>
            <div className='panel-heading'>
                <BrainCircuit aria-hidden='true' />
                <div>
                    <span className='eyebrow'>{translate('settings.eyebrow')}</span>
                    <h2>{translate('settings.title')}</h2>
                </div>
            </div>
            <Tabs
                label={translate('settings.tabs')}
                tabs={[
                    {
                        id: 'analysis',
                        label: translate('settings.tab.analysis'),
                        content: accordion(analysisSections),
                    },
                    {
                        id: 'detector',
                        label: translate('settings.tab.detector'),
                        content: accordion(detectorSections),
                    },
                    {
                        id: 'drawing',
                        label: translate('settings.tab.drawing'),
                        content: accordion(drawingSections),
                    },
                    {
                        id: 'models',
                        label: translate('settings.tab.models'),
                        content: accordion(modelSections),
                    },
                ]}
                activeTab={activeTab}
                onActiveTabChange={setActiveTab}
            />

            <button
                className='run-button'
                type='button'
                disabled={!canRun || isRunning}
                onClick={onRun}
            >
                {translate(isRunning ? 'settings.runRunning' : 'settings.run')}{' '}
                <SlidersHorizontal aria-hidden='true' />
            </button>
            <p
                className='panel-hint'
                aria-live='polite'
            >
                {runHint}
            </p>
        </aside>
    );
}
